import {
  computed,
  DestroyRef,
  DOCUMENT,
  inject,
  Injectable,
  signal,
} from "@angular/core";

import {
  StoredThemeMode,
  THEME_DARK_CLASS,
  THEME_MODE,
  THEME_STORAGE_KEY,
  ThemeMode,
} from "./theme.constants";

/**
 * Owns the admin-web theme: reads the persisted preference (or the OS
 * preference when none is stored), applies/removes the `admin-dark` class on
 * `<html>`, and keeps the choice in sync across tabs. The index.html
 * pre-bootstrap script applies the same class before Angular boots; this
 * service is authoritative from bootstrap onward.
 */
@Injectable({ providedIn: "root" })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly systemPrefersDark = signal(this.readSystemPreference());

  /** The active preference — a persisted explicit mode, or System when no key is stored. */
  readonly mode = signal<ThemeMode>(this.readStoredMode());

  /** Whether the effective theme is dark (explicit preference, or the OS preference in System mode). */
  readonly isDark = computed(() =>
    this.mode() === THEME_MODE.System
      ? this.systemPrefersDark()
      : this.mode() === THEME_MODE.Dark,
  );

  constructor() {
    this.applyEffectiveTheme();
    this.listenForSystemChanges();
    this.listenForCrossTabChanges();
  }

  /**
   * Flips light↔dark and persists the result as an explicit preference —
   * always the opposite of the current *effective* appearance, so toggling
   * out of System mode lands where the user expects.
   */
  toggle(): void {
    const next = this.isDark() ? THEME_MODE.Light : THEME_MODE.Dark;
    this.writeStoredMode(next);
    this.mode.set(next);
    this.applyEffectiveTheme();
  }

  /**
   * Drops the explicit preference: removes the stored key and re-follows the
   * OS `prefers-color-scheme`, re-arming the media-change reaction.
   */
  useSystem(): void {
    this.removeStoredMode();
    this.systemPrefersDark.set(this.readSystemPreference());
    this.mode.set(THEME_MODE.System);
    this.applyEffectiveTheme();
  }

  private applyEffectiveTheme(): void {
    this.document.documentElement.classList.toggle(
      THEME_DARK_CLASS,
      this.isDark(),
    );
  }

  private listenForSystemChanges(): void {
    const media = this.mediaQuery();
    if (!media) return;
    const handler = (event: MediaQueryListEvent): void => {
      // Explicit preferences ignore OS flips; useSystem() re-arms by putting
      // the mode back to System (and re-reading the then-current preference).
      if (this.mode() !== THEME_MODE.System) return;
      this.systemPrefersDark.set(event.matches);
      this.applyEffectiveTheme();
    };
    media.addEventListener("change", handler);
    this.destroyRef.onDestroy(() =>
      media.removeEventListener("change", handler),
    );
  }

  private listenForCrossTabChanges(): void {
    const win = this.document.defaultView;
    if (!win) return;
    const handler = (event: StorageEvent): void => {
      // key === null means storage.clear(); otherwise only react to our key.
      if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
      this.systemPrefersDark.set(this.readSystemPreference());
      this.mode.set(this.readStoredMode());
      this.applyEffectiveTheme();
    };
    win.addEventListener("storage", handler);
    this.destroyRef.onDestroy(() =>
      win.removeEventListener("storage", handler),
    );
  }

  private readStoredMode(): ThemeMode {
    try {
      const stored = this.storage()?.getItem(THEME_STORAGE_KEY);
      return stored === THEME_MODE.Light || stored === THEME_MODE.Dark
        ? stored
        : THEME_MODE.System;
    } catch {
      return THEME_MODE.System;
    }
  }

  private writeStoredMode(mode: StoredThemeMode): void {
    try {
      this.storage()?.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Storage unavailable — the preference lives for this session only.
    }
  }

  private removeStoredMode(): void {
    try {
      this.storage()?.removeItem(THEME_STORAGE_KEY);
    } catch {
      // Storage unavailable — nothing persisted to remove.
    }
  }

  private storage(): Storage | null {
    return this.document.defaultView?.localStorage ?? null;
  }

  private mediaQuery(): MediaQueryList | null {
    const win = this.document.defaultView;
    if (!win || typeof win.matchMedia !== "function") return null;
    return win.matchMedia("(prefers-color-scheme: dark)");
  }

  private readSystemPreference(): boolean {
    try {
      return this.mediaQuery()?.matches ?? false;
    } catch {
      return false;
    }
  }
}
